type GetMaxScaleArgs = {
  /** Dimensions of original thumbnail image. */
  thumbnailWidth: number;
  thumbnailHeight: number;

  /** Dimensions of zoomed image. */
  naturalWidth: number;
  naturalHeight: number;

  /** Pixels of space between screen edge and zoomed image. */
  zoomMargin: number;
};

type GetPopupStyleArgs = GetMaxScaleArgs & {
  /** Position of original thumbnail image. */
  thumbnailTop: number;
  thumbnailLeft: number;

  /** Transition duration (in milliseconds). */
  duration: number;
  isLoaded: boolean;
  isUnloading: boolean;
  /** CSS transforms to preserve (if any). */
  originalTransform?: string;
};

/**
 * Takes a number in milliseconds and returns it as a CSS duration value.
 */
const toDurationString = (duration: number): string => `${duration}ms`;

/**
 * Get the CSS scale value needed to keep the longest edge within the
 * window bounds.
 */
const getScale = ({ height, width, zoomMargin }): number => {
  const scaleX = innerWidth / (width + zoomMargin * 2);
  const scaleY = innerHeight / (height + zoomMargin * 2);
  const scale = Math.min(scaleX, scaleY);

  return scale;
};

/**
 * Get a CSS scale value that fills the available space, without
 * going beyond the intrinsic size of the image.
 */
const getMaxDimensionScale = ({
  thumbnailWidth,
  thumbnailHeight,
  naturalWidth,
  naturalHeight,
  zoomMargin,
}: GetMaxScaleArgs) => {
  const scale = getScale({
    width: naturalWidth,
    height: naturalHeight,
    zoomMargin,
  });
  const ratio =
    naturalWidth > naturalHeight
      ? naturalWidth / thumbnailWidth
      : naturalHeight / thumbnailHeight;

  /**
   * If the scale value is greater than 1 then it will scale larger
   * than its natural size, so we use the ratio of the image
   * dimensions instead.
   */
  return scale > 1 ? ratio : scale * ratio;
};

/**
 * Returns `width`, `height`, `top`, and `left` properties equal to
 * the original image, positioning it in place of the original.
 *
 * Uses these values, as well as the intrinsic dimensions of the large
 * image to determine values to scale and translate by in order to
 * "zoom" from the initial position to fullscreen and centered.
 */
export const getZoomPopupStyle = ({
  thumbnailWidth,
  thumbnailHeight,
  thumbnailTop,
  thumbnailLeft,
  naturalWidth,
  naturalHeight,
  isLoaded,
  isUnloading,
  originalTransform,
  duration,
  zoomMargin,
}: GetPopupStyleArgs) => {
  const durationString = toDurationString(duration);

  if (!isLoaded || isUnloading) {
    const initTransform = [
      `scale(1)`,
      `translate(0, 0)`,
      ...(originalTransform ? [originalTransform] : []),
    ].join(' ');

    return {
      width: thumbnailWidth,
      height: thumbnailHeight,
      top: thumbnailTop,
      left: thumbnailLeft,
      WebkitTransform: initTransform,
      transform: initTransform,
      transitionDuration: durationString,
    };
  }

  const scale = getMaxDimensionScale({
    thumbnailWidth,
    thumbnailHeight,
    naturalWidth,
    naturalHeight,
    zoomMargin,
  });

  // Get the the coords for center of the viewport.
  const viewportX = innerWidth / 2;
  const viewportY = innerHeight / 2;

  // Get the coords for center of the initial image.
  const originalX = thumbnailLeft + thumbnailWidth / 2;
  const originalY = thumbnailTop + thumbnailHeight / 2;

  // Get offset amounts for item coords to be centered on screen
  const translateX = (viewportX - originalX) / scale;
  const translateY = (viewportY - originalY) / scale;

  // Build transform style, including any original transform
  const transform = [
    `scale(${scale})`,
    `translate(${translateX}px, ${translateY}px)`,
    ...(originalTransform ? [originalTransform] : []),
  ].join(' ');

  return {
    width: thumbnailWidth,
    height: thumbnailHeight,
    top: thumbnailTop,
    left: thumbnailLeft,
    WebkitTransform: transform,
    transform,
    transitionDuration: durationString,
  };
};

export const mockElement = {
  getBoundingClientRect: () => ({
    height: 0,
    left: 0,
    top: 0,
    width: 0,
  }),
  style: {
    transform: null,
  },
};
